/**
 * Training Plan Setup (intake) API client.
 *
 * Talks only to /api/athlete/training-intake — these endpoints are outside
 * the AI Coach path so questionnaire interactions never consume the ask
 * quota. Keep this module free of any ai-coach imports.
 */
import { http } from "@/lib/api";

const INTAKE_ROOT = "/athlete/training-intake";

const normaliseError = (err, operation) => {
  if (err?.name === "CanceledError" || err?.code === "ERR_CANCELED") {
    return { code: "aborted", message: "Request cancelled" };
  }
  if (err?.message === "Network Error" || err?.code === "ERR_NETWORK") {
    return {
      code: "network",
      message: "Cannot connect to the Endurava server.",
    };
  }
  const status = err?.response?.status;
  if (status === 401) {
    return {
      code: "unauthenticated",
      status,
      message: "Your session has expired. Please sign in again.",
    };
  }
  const detail = err?.response?.data?.detail;
  // FastAPI/Pydantic validation errors come back as an array of
  // ``{ loc, msg, type }`` entries. Surfacing them verbatim is much
  // more actionable than the previous generic "Could not save…"
  // fallback — the athlete now sees "availability.days.0.max_minutes:
  // ensure this value is less than or equal to 600" instead of a
  // red placeholder.
  if (Array.isArray(detail)) {
    const parts = detail
      .map((entry) => {
        if (!entry) return null;
        const loc = Array.isArray(entry.loc)
          ? entry.loc.filter((seg) => seg !== "body").join(".")
          : "";
        const msg = entry.msg || entry.message || entry.type || "invalid value";
        return loc ? `${loc}: ${msg}` : msg;
      })
      .filter(Boolean);
    if (parts.length) {
      return {
        code: "validation",
        status,
        message: `Could not ${operation || "process"} training plan intake — ${parts.join("; ")}.`,
        detail,
      };
    }
  }
  if (detail && typeof detail === "object") {
    const code = detail.code || "unknown";
    const message =
      detail.message
      || `Could not ${operation || "process"} training plan intake.`;
    return { code, status, message, detail };
  }
  const message =
    (typeof detail === "string" && detail)
    || `Could not ${operation || "process"} training plan intake.`;
  return { code: "unknown", status, message };
};

const wrap = (err, operation) => {
  const info = normaliseError(err, operation);
  const wrapped = new Error(info.message);
  wrapped.trainingIntakeError = info;
  return wrapped;
};

export const fetchTrainingIntake = async () => {
  try {
    const response = await http.get(INTAKE_ROOT);
    return response?.data || null;
  } catch (err) {
    throw wrap(err, "load");
  }
};

export const saveTrainingIntakeDraft = async (partial) => {
  try {
    const response = await http.put(`${INTAKE_ROOT}/draft`, partial || {});
    return response?.data || null;
  } catch (err) {
    throw wrap(err, "save");
  }
};

export const commitTrainingIntake = async (payload) => {
  try {
    const response = await http.post(`${INTAKE_ROOT}/commit`, payload);
    return response?.data || null;
  } catch (err) {
    throw wrap(err, "commit");
  }
};

/**
 * Ask the backend to resolve the wizard's in-flight start-preference
 * answer into the deterministic ``PlanStartResolution`` (requested +
 * effective + first-session dates). The frontend NEVER computes plan
 * start dates locally — backend is the sole authority (see
 * ATHLETE-CONTROLLED TRAINING PLAN START V1). Returns ``null`` when
 * the athlete hasn't yet selected any available training days (the
 * wizard shows a gentle prompt in that case).
 */
export const previewPlanStart = async (
  { startPreference, customStartDate, activePlanConflictChoice } = {},
) => {
  try {
    const response = await http.post(`${INTAKE_ROOT}/plan-start-preview`, {
      start_preference: startPreference || null,
      custom_start_date: customStartDate || null,
      active_plan_conflict_choice: activePlanConflictChoice || null,
    });
    return response?.data || null;
  } catch (err) {
    const info = normaliseError(err, "preview plan start for");
    if (info.status === 422 && err?.response?.data?.detail?.code
        === "availability_required") {
      // Not an error — the wizard just hasn't captured availability
      // yet. Return null so the caller can render the "select your
      // training days first" hint without a red banner.
      return null;
    }
    const wrapped = new Error(info.message);
    wrapped.trainingIntakeError = info;
    throw wrapped;
  }
};

export const refreshReadiness = async (readiness) => {
  try {
    const response = await http.post(`${INTAKE_ROOT}/readiness`, {
      readiness: readiness || {},
    });
    return response?.data || null;
  } catch (err) {
    throw wrap(err, "refresh readiness for");
  }
};
